import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import GeoScoreCard from '../components/GeoScoreCard';
import CategoryBar from '../components/CategoryBar';

const SEVERITY_STYLE = {
  fail: { bg: 'var(--red-glow)', border: 'var(--red)', color: 'var(--red)', icon: '✗' },
  warn: { bg: 'var(--orange-glow)', border: 'var(--orange)', color: 'var(--orange)', icon: '⚠' },
  pass: { bg: 'var(--green-glow)', border: 'var(--green)', color: 'var(--green)', icon: '✓' },
  info: { bg: 'var(--accent-glow)', border: 'var(--accent)', color: 'var(--accent)', icon: 'ℹ' },
};
const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

export default function GeoAuditPage() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issueFilter, setIssueFilter] = useState('all');
  const [showRecs, setShowRecs] = useState(true);

  useEffect(() => { loadAudit(); }, [auditId]);

  async function loadAudit() {
    try { setAudit(await api.getGeoAudit(auditId)); }
    catch (err) { console.error('Failed to load audit:', err); }
    setLoading(false);
  }

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>טוען תוצאות Audit...</div>;
  if (!audit) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Audit לא נמצא</div>;

  // Map from API structure: categories is an object with nested score
  const cats = audit.categories || {};
  const categories = [
    { key: 'ai_accessibility',   score: cats.ai_accessibility?.score ?? audit.ai_accessibility_score ?? 0 },
    { key: 'content_structure',  score: cats.content_structure?.score ?? audit.content_structure_score ?? 0 },
    { key: 'schema_markup',      score: cats.schema_markup?.score ?? audit.schema_markup_score ?? 0 },
    { key: 'factual_density',    score: cats.factual_density?.score ?? audit.factual_density_score ?? 0 },
    { key: 'freshness',          score: cats.freshness?.score ?? audit.freshness_score ?? 0 },
    { key: 'entity_clarity',     score: cats.entity_clarity?.score ?? audit.entity_clarity_score ?? 0 },
    { key: 'faq_alignment',      score: cats.faq_alignment?.score ?? audit.faq_alignment_score ?? 0 },
    { key: 'vocabulary_fluency', score: cats.vocabulary_fluency?.score ?? audit.vocabulary_fluency_score ?? 0 },
  ];

  const issues = audit.issues || [];
  const recommendations = audit.recommendations || [];
  const filteredIssues = issueFilter === 'all' ? issues
    : issues.filter(i => (i.severity || i.status) === issueFilter);
  const sortedRecs = [...recommendations].sort(
    (a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9)
  );

  const failCount = issues.filter(i => (i.severity || i.status) === 'fail').length;
  const warnCount = issues.filter(i => (i.severity || i.status) === 'warn').length;
  const passCount = issues.filter(i => (i.severity || i.status) === 'pass').length;

  // API returns page_url/scan_date, fallback to url/created_at
  const pageUrl = audit.page_url || audit.url || '';
  const scanDate = audit.scan_date || audit.created_at || '';

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <button className="btn btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: '16px' }}>
        → חזרה
      </button>

      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>GEO Audit</h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0', direction: 'ltr', textAlign: 'right' }}>{pageUrl}</p>
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '2px 0 0' }}>
          {scanDate ? new Date(scanDate).toLocaleString('he-IL') : ''}
        </p>
      </div>

      {/* Score + Counts */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', alignItems: 'center' }}>
        <GeoScoreCard score={audit.overall_score} size={140} label="ציון כולל" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', flex: 1 }}>
          <CountCard value={failCount} label="נכשלו" color="var(--red)" />
          <CountCard value={warnCount} label="אזהרות" color="var(--orange)" />
          <CountCard value={passCount} label="עברו" color="var(--green)" />
          <CountCard value={recommendations.length} label="המלצות" color="var(--accent)" />
        </div>
      </div>

      {/* Categories */}
      <div className="section-card" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>ציונים לפי קטגוריה</h3>
        {categories.map(c => <CategoryBar key={c.key} categoryKey={c.key} score={c.score} />)}
      </div>

      {/* Platform Scores */}
      {audit.platform_scores && Object.keys(audit.platform_scores).length > 0 && (
        <div className="section-card" style={{ padding: '20px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>ציונים לפי פלטפורמת AI</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {Object.entries(audit.platform_scores).map(([p, s]) => (
              <div key={p} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <GeoScoreCard score={s} size={64} showLabel={false} />
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{p}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues */}
      <div className="section-card" style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>בדיקות ({issues.length})</h3>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[['all','הכל'],['fail','נכשל'],['warn','אזהרה'],['pass','עבר']].map(([k,v]) => (
              <button key={k} onClick={() => setIssueFilter(k)}
                className={'filter-chip' + (issueFilter === k ? ' active' : '')}>{v}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {filteredIssues.map((issue, idx) => {
            const sev = issue.severity || issue.status || 'info';
            const st = SEVERITY_STYLE[sev] || SEVERITY_STYLE.info;
            return (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px',
                borderRadius: 'var(--radius-sm)', border: '1px solid ' + st.border, background: st.bg,
              }}>
                <span style={{ color: st.color, fontWeight: 700, fontSize: '14px', width: '18px', textAlign: 'center', flexShrink: 0 }}>
                  {st.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>
                    {issue.check || issue.message || issue.description}
                  </div>
                  {issue.detail && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{issue.detail}</div>}
                  {issue.category && (
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'var(--surface-hover)',
                      padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>{issue.category}</span>
                  )}
                </div>
              </div>
            );
          })}
          {filteredIssues.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>אין תוצאות לפילטר הנבחר</div>
          )}
        </div>
      </div>

      {/* Recommendations */}
      <div className="section-card" style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: showRecs ? '12px' : 0, cursor: 'pointer' }} onClick={() => setShowRecs(!showRecs)}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>המלצות לשיפור ({recommendations.length})</h3>
          <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{showRecs ? '▴' : '▾'}</span>
        </div>
        {showRecs && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sortedRecs.map((rec, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px',
                borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg)',
              }}>
                <PriorityBadge priority={rec.priority} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>
                    {rec.action || rec.text || rec.recommendation || rec.description}
                  </div>
                  {rec.category && (
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'var(--surface-hover)',
                      padding: '2px 6px', borderRadius: '4px', marginTop: '6px', display: 'inline-block' }}>{rec.category}</span>
                  )}
                  {rec.auto_fixable && (
                    <span style={{ fontSize: '10px', color: 'var(--green)', background: 'var(--green-glow)',
                      padding: '2px 6px', borderRadius: '4px', marginTop: '6px', marginRight: '6px', display: 'inline-block' }}>ניתן לתיקון אוטומטי</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CountCard({ value, label, color }) {
  return (
    <div className="section-card" style={{ padding: '14px', textAlign: 'center' }}>
      <div style={{ fontSize: '24px', fontWeight: 800, fontFamily: 'var(--mono)', color }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{label}</div>
    </div>
  );
}

function PriorityBadge({ priority }) {
  const m = {
    critical: { bg: 'var(--red-glow)', color: 'var(--red)', text: 'קריטי' },
    high: { bg: 'var(--orange-glow)', color: 'var(--orange)', text: 'גבוה' },
    medium: { bg: 'var(--accent-glow)', color: 'var(--accent)', text: 'בינוני' },
    low: { bg: 'var(--surface-hover)', color: 'var(--text-secondary)', text: 'נמוך' },
  };
  const c = m[priority] || m.medium;
  return (
    <span style={{ fontSize: '10px', fontWeight: 600, color: c.color, background: c.bg,
      padding: '3px 8px', borderRadius: '4px', flexShrink: 0, marginTop: '2px' }}>{c.text}</span>
  );
}
